/* ExposureDetailsViewComponent.js */

const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ExposureDetailsViewRenderer");
const ExposureNavMemory = require("../../ExposureNavMemory");

// Wide ExposureRule projection for the justification bundle: predicate clauses,
// provenance, and extraction telemetry (incl. rawResponse model output). A
// narrow projection silently empties bundle sections.
const RULE_PROJECTION = {
    id: true,
    advisoryId: true,
    advisoryName: true,
    status: true,
    reviewStatus: true,
    staleForReview: true,
    version: true,
    predicate: true,
    provenance: true,
    extraction: true,
    lastFailedExtraction: true,
    extractionError: true,
    extractedAt: true,
    extractedByModel: true,
    llmConnectionId: true,
    advisorySource: true,
};

const DEVICE_TAGS_PROJECTION = {
    deviceId: true,
    tags: true,
    tagRulesVersion: true,
    generatedAt: true,
    sourceConfigHash: true,
};

class ExposureDetailsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        state,
        userSession,
        MUF,
        DeviceEntity,
        steveIo,
        // Appended, never inserted — $inject below is positional against this signature.
        TriggerTypeEntity
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;
        this._userSession = userSession;
        this._DeviceEntity = DeviceEntity;
        // Runtime evidence is collected by firing the same on-demand qualifyCves schedule the
        // Exposure tab uses, so the drawer needs the TriggerType class the launcher reuses.
        this._TriggerTypeEntity = TriggerTypeEntity;
        this._io = steveIo;
        this._activeOrgId = userSession.activeOrganization.id;
        this._cveId = state.cveId;
        this._device = undefined;
        this._entry = undefined;
        // Every verdict row for this device, kept so stepping to the next CVE costs no
        // qualification re-read — the entity is one document holding all of them.
        this._rows = [];
        this._advisoriesById = new Map();
        this._rule = undefined;

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();

        return this._renderer.shutdown().then(
            () => {
                this._modalUserFeedback = undefined;
                this._userSession = undefined;
                this._DeviceEntity = undefined;
                this._io = undefined;
                this._device = undefined;
                this._entry = undefined;
                this._rows = undefined;
                this._advisoriesById = undefined;
                this._rule = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ExposureDetailsViewComponent.shutdown() error.");
            }
        );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    /**
     * Loads the qualification entry for this drawer's CVE (viewState.cveId),
     * plus best-effort advisory metadata and the winning advisory's
     * ExposureRule (for the remediation trust badge). The entry is the
     * essential data — advisory/rule loads never reject the view.
     */
    whenModelIsReady() {
        return this._gameEditor
            .whenDeviceIsReady()
            .then(() => {
                this._device = this._gameEditor.getDevice();
                return this._DeviceEntity.load(
                    this._activeOrgId,
                    "DeviceCveQualification",
                    this._device.getId()
                );
            })
            .then((qualification) => {
                const props =
                    qualification && typeof qualification.getPristineProperties === "function"
                        ? qualification.getPristineProperties()
                        : null;
                const rows = (props && props.qualifications) || [];
                this._rows = rows;
                this._entry = rows.find((row) => row.cveId === this._cveId);
                if (!this._entry) {
                    return Promise.reject("NoEntry");
                }
                // Also stamped when the table row is clicked; repeated here so a drawer re-entered
                // from an advisory breadcrumb still tells the list where to return to.
                ExposureNavMemory.remember(this.getDeviceId(), {focusCveId: this._cveId});
                this._registerListeners();
                return Promise.all([
                    this._loadAdvisories(),
                    this._loadRule(),
                    this._loadConfigSnapshots(),
                ]);
            })
            .catch((err) => {
                if (err !== "NoEntry") {
                    this._logger.warn(err, "ExposureDetailsViewComponent.whenModelIsReady() error");
                }
                return Promise.reject(err);
            });
    }

    /**
     * The device's stored raw configs, so the drawer can say whether the verdict scored the
     * configuration the device still holds.
     *
     * hash + createdOn ONLY — never the capture itself. A running config carries credentials, and
     * this drawer is not the place to put them; the id and the timestamp answer the question
     * without moving any device text into this view or the exports it feeds.
     *
     * Best-effort: a failure leaves the list empty and the snapshot line simply does not render.
     */
    _loadConfigSnapshots() {
        this._configSnapshots = [];
        return this._DeviceEntity
            .loadByQuery(
                this._activeOrgId,
                "RawConfig",
                {deviceId: this.getDeviceId()},
                {hash: true, createdOn: true}
            )
            .then((rows) => {
                this._configSnapshots = (rows || []).map((row) => ({
                    hash: typeof row.getHash === "function" ? row.getHash() : row.hash,
                    createdOn:
                        typeof row.getCreatedOn === "function" ? row.getCreatedOn() : row.createdOn,
                }));
                return null;
            })
            .catch((err) => {
                this._logger.warn(err, "ExposureDetailsViewComponent._loadConfigSnapshots() error");
                return null;
            });
    }

    getConfigSnapshots() {
        return this._configSnapshots || [];
    }

    /** Best-effort advisory metadata (winning + contributing). Never rejects. */
    _loadAdvisories() {
        this._advisoriesById = new Map();
        const ids = new Set();
        if (this._entry.advisoryId) {
            ids.add(this._entry.advisoryId);
        }
        (this._entry.contributingAdvisoryIds || []).forEach((id) => {
            if (id) {
                ids.add(id);
            }
        });
        if (ids.size === 0) {
            return Promise.resolve();
        }
        return this._DeviceEntity
            .loadByQuery(
                this._activeOrgId,
                "Advisory",
                {id: {$in: Array.from(ids)}},
                {id: true, displayName: true, data: true}
            )
            .then((advisories) => {
                (advisories || []).forEach((advisory) => {
                    const advProps =
                        typeof advisory.getPristineProperties === "function"
                            ? advisory.getPristineProperties()
                            : advisory;
                    const data = (advProps && advProps.data) || {};
                    // Entity id is NOT a pristine property — read it via the
                    // getter or the map silently stays empty (blank severity/CVSS).
                    const advisoryId =
                        typeof advisory.getId === "function"
                            ? advisory.getId()
                            : advProps && advProps.id;
                    if (advisoryId) {
                        this._advisoriesById.set(advisoryId, {
                            advisoryId: advisoryId,
                            displayName: (advProps && advProps.displayName) || null,
                            sir: data.sir || null,
                            firstFixed: data.firstFixed || null,
                            advisoryTitle: data.advisoryTitle || null,
                            cvssBaseScore: data.cvssBaseScore || null,
                        });
                    }
                });
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ExposureDetailsViewComponent._loadAdvisories() error — advisory metadata unavailable"
                );
                this._advisoriesById = new Map();
            });
    }

    /**
     * Best-effort ExposureRule for the winning advisory: supplies the
     * remediation trust badge (reviewStatus / staleForReview). Never rejects —
     * without the rule the drawer falls back to the entry's snapshotted text
     * with an 'unreviewed' caption.
     */
    _loadRule() {
        this._rule = undefined;
        if (!this._entry.advisoryId) {
            return Promise.resolve();
        }
        return this._DeviceEntity
            .loadByQuery(
                this._activeOrgId,
                "ExposureRule",
                {advisoryId: this._entry.advisoryId},
                {
                    id: true,
                    advisoryId: true,
                    reviewStatus: true,
                    staleForReview: true,
                    predicate: true,
                    version: true,
                    status: true,
                    extractedAt: true,
                    extractedByModel: true,
                    extractionError: true,
                }
            )
            .then((rules) => {
                const rule = (rules || [])[0];
                if (!rule) {
                    return;
                }
                const props =
                    typeof rule.getPristineProperties === "function"
                        ? rule.getPristineProperties()
                        : rule;
                this._rule = {
                    reviewStatus: props.reviewStatus || "unreviewed",
                    staleForReview: props.staleForReview === true,
                    recommendedRemediation:
                        (props.predicate && props.predicate.recommendedRemediation) || null,
                    // Extraction provenance for the drawer's audit section.
                    version:
                        props.version !== null && props.version !== undefined
                            ? props.version
                            : null,
                    status: props.status || null,
                    extractedAt: props.extractedAt || null,
                    extractedByModel: props.extractedByModel || null,
                    extractionError:
                        (props.extractionError && props.extractionError.message) || null,
                };
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ExposureDetailsViewComponent._loadRule() error — rule metadata unavailable"
                );
                this._rule = undefined;
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getCveId() {
        return this._cveId;
    }

    getEntry() {
        return this._entry;
    }

    getDeviceId() {
        try {
            return (this._device && this._device.getId()) || null;
        } catch (err) {
            this._logger.warn(err, "ExposureDetailsViewComponent.getDeviceId() error");
            return null;
        }
    }

    /**
     * The CVEs this drawer can step through, in the order the operator saw them in the Exposure
     * table — the table records that order, because it is the one they are working down and it is
     * ranked by advisory severity this view never loads in full.
     *
     * Falls back to the qualification's own row order when the table order is unavailable (the
     * drawer re-entered from an advisory breadcrumb in a fresh session, say). Stepping in an
     * unfamiliar order is a much smaller failure than a drawer that cannot step at all.
     *
     * @returns {String[]} CVE ids, filtered to rows this device actually carries.
     */
    getOrderedCveIds() {
        const rows = this._rows || [];
        const present = new Set(rows.map((row) => row.cveId).filter(Boolean));
        const remembered = ExposureNavMemory.recall(this.getDeviceId()).order.filter((cveId) =>
            present.has(cveId)
        );
        if (remembered.length === present.size) {
            return remembered;
        }
        return rows.map((row) => row.cveId).filter(Boolean);
    }

    /**
     * Re-points the drawer at another CVE of the same device, reusing the qualification already in
     * hand: only the new CVE's advisory metadata and rule are fetched. That is what makes stepping
     * feel like paging through a list rather than reopening a view — a full re-navigation would tear
     * this view down, rebuild it, and re-read the qualification for every step.
     *
     * @param {String} cveId Must be one of getOrderedCveIds().
     * @returns {Promise} Rejects with "NoEntry" when the device carries no such verdict, leaving the
     *   drawer on the CVE it was already showing.
     */
    stepTo(cveId) {
        const entry = (this._rows || []).find((row) => row.cveId === cveId);
        if (!entry) {
            return Promise.reject("NoEntry");
        }
        this._cveId = cveId;
        this._entry = entry;
        ExposureNavMemory.remember(this.getDeviceId(), {focusCveId: cveId});
        return Promise.all([this._loadAdvisories(), this._loadRule()]);
    }

    getAdvisoriesById() {
        return this._advisoriesById;
    }

    getRule() {
        return this._rule;
    }

    getDevice() {
        return this._device;
    }

    getIo() {
        return this._io;
    }

    getUserSession() {
        return this._userSession;
    }

    getActiveOrgId() {
        return this._activeOrgId;
    }

    getTriggerTypeEntity() {
        return this._TriggerTypeEntity;
    }

    getUserFeedback() {
        return this._modalUserFeedback;
    }

    /**
     * Lazily loads the full evidence set for the justification-bundle download:
     * advisories widened to include the CPE `nist` block, every contributing
     * advisory's ExposureRule (wide projection incl. extraction telemetry +
     * model output), the device's capability tags (tag fields only, never the parsed
     * config itself), and the org's ExposureManagementPrefs. Best-effort per source
     * — a failed load degrades that section rather than failing the whole download.
     * Not run on drawer open; only when the download button is clicked.
     */
    loadJustificationData() {
        const entry = this._entry || {};
        const ids = [];
        if (entry.advisoryId) {
            ids.push(entry.advisoryId);
        }
        (entry.contributingAdvisoryIds || []).forEach((id) => {
            if (id && ids.indexOf(id) < 0) {
                ids.push(id);
            }
        });

        const loadAdvisories =
            ids.length === 0
                ? Promise.resolve(new Map())
                : this._DeviceEntity
                      .loadByQuery(
                          this._activeOrgId,
                          "Advisory",
                          {id: {$in: ids}},
                          {id: true, displayName: true, data: true, nist: true}
                      )
                      .then((advisories) => {
                          const map = new Map();
                          (advisories || []).forEach((advisory) => {
                              const props =
                                  typeof advisory.getPristineProperties === "function"
                                      ? advisory.getPristineProperties()
                                      : advisory;
                              const advisoryId =
                                  typeof advisory.getId === "function"
                                      ? advisory.getId()
                                      : props && props.id;
                              if (advisoryId) {
                                  map.set(advisoryId, {
                                      advisoryId: advisoryId,
                                      displayName: (props && props.displayName) || null,
                                      data: (props && props.data) || null,
                                      nist: (props && props.nist) || null,
                                  });
                              }
                          });
                          return map;
                      })
                      .catch((err) => {
                          this._logger.warn(
                              {error: err},
                              "ExposureDetailsViewComponent.loadJustificationData() advisory load failed"
                          );
                          return new Map();
                      });

        const loadRules =
            ids.length === 0
                ? Promise.resolve(new Map())
                : this._DeviceEntity
                      .loadByQuery(
                          this._activeOrgId,
                          "ExposureRule",
                          {advisoryId: {$in: ids}},
                          RULE_PROJECTION
                      )
                      .then((rules) => {
                          const map = new Map();
                          (rules || []).forEach((rule) => {
                              const props =
                                  typeof rule.getPristineProperties === "function"
                                      ? rule.getPristineProperties()
                                      : rule;
                              if (props && props.advisoryId) {
                                  map.set(props.advisoryId, props);
                              }
                          });
                          return map;
                      })
                      .catch((err) => {
                          this._logger.warn(
                              {error: err},
                              "ExposureDetailsViewComponent.loadJustificationData() rule load failed"
                          );
                          return new Map();
                      });

        const loadDeviceTags =
            this._device && typeof this._device.getId === "function"
                ? this._DeviceEntity
                      .loadByQuery(
                          this._activeOrgId,
                          "DeviceParsedConfig",
                          {deviceId: this._device.getId()},
                          DEVICE_TAGS_PROJECTION
                      )
                      .then((rows) => {
                          const row = (rows || [])[0];
                          const props =
                              row && typeof row.getPristineProperties === "function"
                                  ? row.getPristineProperties()
                                  : row;
                          return {deviceTags: props || null, error: null};
                      })
                      .catch((err) => {
                          this._logger.warn(
                              {error: err},
                              "ExposureDetailsViewComponent.loadJustificationData() device tag load failed"
                          );
                          return {deviceTags: null, error: err || new Error("unknown error")};
                      })
                : Promise.resolve({deviceTags: null, error: {errorCode: "NoDevice"}});

        const loadPrefs = this._DeviceEntity
            .loadByQuery(
                this._activeOrgId,
                "ExposureManagementPrefs",
                {},
                {
                    id: true,
                    captureExtractionTranscript: true,
                    runtimeDeepeningMode: true,
                    deepeningMode: true,
                }
            )
            .then((rows) => {
                const prefs = (rows || [])[0];
                if (!prefs) {
                    return null;
                }
                return typeof prefs.getPristineProperties === "function"
                    ? prefs.getPristineProperties()
                    : prefs;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ExposureDetailsViewComponent.loadJustificationData() prefs load failed"
                );
                return null;
            });

        return Promise.all([loadAdvisories, loadRules, loadDeviceTags, loadPrefs]).then(
            (results) => ({
                advisoriesById: results[0],
                rulesByAdvisoryId: results[1],
                deviceTags: results[2].deviceTags,
                deviceTagsError: results[2].error,
                prefs: results[3],
            })
        );
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = ExposureDetailsViewComponent;

ExposureDetailsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.entities.Device",
    "$teve.io",
    "$teve.entities.TriggerType",
];

ExposureDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "Exposure Details",
    icon: "devicemanager.qualify_cves",
    shown: true,
};
